import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * PortalDropdown - Stripe/Linear Standard (2025)
 * 
 * Renders dropdown menu in a portal to escape table overflow.
 * Uses viewport-based positioning with smart edge detection.
 * Never clips, always visible.
 */
export default function PortalDropdown({
  isOpen,
  onClose,
  triggerRef,
  children,
  align = 'right',
}) {
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [placement, setPlacement] = useState({ vertical: 'bottom', horizontal: 'right' });

  // Calculate position when opened
  useEffect(() => {
    if (!isOpen || !triggerRef.current || !dropdownRef.current) return;

    const calculatePosition = () => {
      const trigger = triggerRef.current.getBoundingClientRect();
      const dropdown = dropdownRef.current.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      // Default positioning
      let top = trigger.bottom + 4; // 4px gap
      let left = trigger.right - 208; // 208px = dropdown width (52 * 4)

      // Vertical placement (flip if near bottom)
      const spaceBelow = viewport.height - trigger.bottom;
      const spaceAbove = trigger.top;
      const dropdownHeight = Math.min(dropdown.height || 350, 350);

      let verticalPlacement = 'bottom';
      if (spaceBelow < dropdownHeight + 20 && spaceAbove > dropdownHeight) {
        // Not enough space below, but enough above → flip
        top = trigger.top - dropdownHeight - 4;
        verticalPlacement = 'top';
      }

      // Horizontal placement (shift if near right edge)
      let horizontalPlacement = align;
      if (align === 'right') {
        // Default: align right edge of dropdown with right edge of trigger
        left = trigger.right - 208;
        
        // If dropdown would overflow left edge
        if (left < 8) {
          left = 8; // 8px from left edge
          horizontalPlacement = 'left';
        }
      } else {
        // Align left
        left = trigger.left;
        
        // If dropdown would overflow right edge
        if (left + 208 > viewport.width - 8) {
          left = viewport.width - 208 - 8;
          horizontalPlacement = 'right';
        }
      }

      // Ensure dropdown stays within viewport
      if (left + 208 > viewport.width - 8) {
        left = viewport.width - 208 - 8;
      }
      if (left < 8) {
        left = 8;
      }

      setPosition({ top, left });
      setPlacement({ vertical: verticalPlacement, horizontal: horizontalPlacement });
    };

    // Calculate immediately
    calculatePosition();

    // Recalculate on scroll or resize
    const handleUpdate = () => {
      if (isOpen) {
        calculatePosition();
      }
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, triggerRef, align]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        onClose();
      }
    };

    // Close on Escape
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed w-52 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50 text-[13px] max-h-[350px] overflow-y-auto"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
}
